import React, { useEffect, useState } from 'react';
import { debounce } from 'lodash';
import Downshift from 'downshift';
import { FormattedMessage } from '@/utils/i18n';
import { HexColorPicker } from 'react-colorful';
import { Button } from '../buttons';
import ColorTextBox from './ColorTextBox';

const DEFAULT_COLOR = '#ffffff';
const DEBOUNCE_DELAY = 200;

type Props = {
  selected?: string;
  onChange?: (color: string) => void | Promise<void>;
  dropdown?: boolean;
};

const ColorInput = ({ selected, onChange, dropdown, ...rest }: Props) => {
  const [color, setColor] = useState(selected);
  const [tempColor, setTempColor] = useState(selected || DEFAULT_COLOR);

  useEffect(() => {
    setColor(selected);
    setTempColor(selected || DEFAULT_COLOR);
  }, [selected]);

  const debouncedOnChange = debounce(async (color: string) => {
    if (onChange) {
      try {
        await onChange(color);
      } catch (error) {
        setColor(selected);
        setTempColor(selected || DEFAULT_COLOR);
      }
    }
  }, DEBOUNCE_DELAY);

  const handleChange = (color: string) => {
    setColor(color);
    setTempColor(color);
    debouncedOnChange(color);
  };

  if (!dropdown) {
    return (
      <div className="color-input" {...rest}>
        <ColorTextBox color={color} onChange={handleChange} />
      </div>
    );
  }

  return (
    <div className="color-input" {...rest}>
      <Downshift onOuterClick={() => setTempColor(color || DEFAULT_COLOR)}>
        {({ isOpen, openMenu, closeMenu }) => {
          const cancel = () => {
            closeMenu();
            setTempColor(color || DEFAULT_COLOR);
          };

          const apply = () => {
            closeMenu();
            handleChange(tempColor);
          };

          return (
            <div>
              <ColorTextBox color={color} onChange={handleChange} onFocus={() => openMenu()} onPreviewClick={() => openMenu()} />

              {isOpen && (
                <div className="input-dropdown">
                  <div className="picker">
                    <HexColorPicker color={tempColor} onChange={setTempColor} className="color-gradientpicker" />
                    <ColorTextBox color={tempColor} onChange={setTempColor} />
                  </div>
                  <div className="actions">
                    <Button onClick={cancel}>
                      <FormattedMessage id="Common.Cancel" />
                    </Button>
                    <Button className="cta" onClick={apply}>
                      <FormattedMessage id="Common.Apply" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        }}
      </Downshift>
    </div>
  );
};

export default ColorInput;
